/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'

import {
  EuiButtonIcon,
  EuiCheckbox,
  EuiPopover,
  EuiPopoverTitle,
  EuiSpacer,
  EuiText,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useToggle } from '@modules/utils/hooks/useToggle'

import messages from '../messages'

import type { FunctionComponent, ReactNode } from 'react'

export type Props = {
  isChecked: boolean
  onDryRunChange: (b: boolean) => void
  label: ReactNode
  description: ReactNode
  help: ReactNode
}

const DryRunCheck: FunctionComponent<Props> = ({
  isChecked,
  onDryRunChange,
  label,
  description,
  help,
}) => (
  <EuiCheckbox
    data-test-id='dry-run-checkbox'
    id='edit-deployment-template.dry-run-checkbox'
    label={
      <Fragment>
        <DryRunCheckDescription label={label} description={description} />

        <EuiSpacer size='xs' />

        <EuiText color='subdued' size='s'>
          {help}
        </EuiText>
      </Fragment>
    }
    checked={isChecked}
    onChange={() => onDryRunChange(!isChecked)}
  />
)

const DryRunCheckDescription: FunctionComponent<{
  label: ReactNode
  description: ReactNode
}> = ({ label, description }) => {
  const [isPopoverOpen, togglePopover, setPopoverOpen] = useToggle()

  return (
    <React.Fragment>
      {label}
      <EuiPopover
        anchorPosition='upCenter'
        button={
          <EuiButtonIcon
            iconType='info'
            color='primary'
            aria-label='Info'
            onClick={togglePopover}
          />
        }
        className='fs-unmask'
        isOpen={isPopoverOpen}
        closePopover={() => setPopoverOpen(false)}
      >
        <EuiPopoverTitle>
          <FormattedMessage {...messages.whenToUseDryRun} />
        </EuiPopoverTitle>

        <div style={{ maxWidth: '300px' }}>
          <EuiText size='s'>{description}</EuiText>
        </div>
      </EuiPopover>
    </React.Fragment>
  )
}

export default DryRunCheck
